import { useState } from 'react'
import { v4 as uuid } from 'uuid'
import { useTranslation } from 'react-i18next'
import {
  OG_IMAGE,
  COVER_IMAGE,
  OG_IMAGE_API_CROPPED_WIDTH,
  OG_IMAGE_API_CROPPED_HEIGHT,
  COVER_IMAGE_API_CROPPED_WIDTH,
  COVER_IMAGE_API_CROPPED_HEIGHT,
} from './ImageConstant'
import { useArticleContext } from '../_core'
import { validImageTypes } from './image-upload.services'
import { handleImageFile } from './handleImageFile'
import { ImageInfo } from '../_shared/image-upload.services'
import { UploadTargetType } from '../_shared/image-upload.services'

type UseFileUploaderInputType = {
  selectedFiles?: Array<ImageInfo>
  setSelectedFiles?: (files: Array<ImageInfo>) => void
  maxImageCount?: number
  imageType?: string
  onImageAction?: () => void
  target?: UploadTargetType
}

export const useFilesUploader = (input: UseFileUploaderInputType) => {
  const { maxImageCount, selectedFiles, imageType, onImageAction, setSelectedFiles, target } = input
  const [errorMessages, setErrorMessages] = useState([])
  const [imageUploadStartCount, setImageUploadStartCount] = useState(0)
  const [imageUploadEndCount, setImageUploadEndCount] = useState(0)
  const { updateImageIds, updateCoverImageId, updateOgImageId, articleState } = useArticleContext()
  const { t } = useTranslation('imageInput')

  const validateFile = (file) => {
    const updateErrorMessages = (message) => {
      return setErrorMessages((prevArray) => {
        const idx = prevArray.findIndex((val) => val === message)
        if (idx === -1) {
          return prevArray.concat(message)
        } else {
          return [...prevArray.slice(0, idx), message, ...prevArray.slice(idx + 1)]
        }
      })
    }

    let validated = true
    const validTypes = validImageTypes
    if (validTypes.indexOf(file.type) === -1) {
      const invalidFileTypeMessage = t([
        'userErrorMessage.invalidFileType',
        '圖片格式僅支援 jpg、jpeg、png。',
      ])
      updateErrorMessages(invalidFileTypeMessage)
      validated = false
    }
    const maxFileSizeInMB = 5
    if (file.size > maxFileSizeInMB * 1024 * 1024) {
      const invalidFileSizeMessage = t(
        ['userErrorMessage.invalidFileSize', `圖片不得超過 ${maxFileSizeInMB}MB`],
        {
          max: maxFileSizeInMB,
        },
      )
      updateErrorMessages(invalidFileSizeMessage)
      validated = false
    }
    if (validated) {
      return true
    }
    return false
  }

  const handleFiles = async (files) => {
    setErrorMessages([])
    const filesArray = Array.from(files)
    type File = {
      id?: string
    }
    const newFiles = filesArray.filter((file: File) => {
      if (!selectedFiles || selectedFiles.length === 0) return file
      for (const selectedFile of selectedFiles) {
        return file.id !== selectedFile.id
      }
    })

    if (newFiles.length > maxImageCount) {
      setErrorMessages([
        t(['userErrorMessage.tooManySelectedFiles', `最多只能上傳 ${maxImageCount} 張圖片`], {
          max: maxImageCount,
        }),
      ])
      return
    }

    setImageUploadStartCount((prev) => prev + 1)
    for (const file of files) {
      if (validateFile(file) === false) continue
      const id = uuid()
      if (!file.id) {
        file.id = id
      }
      const formData = new FormData()
      const croppedSize: Record<string, unknown> = {
        fit: 'cover',
      }
      switch (imageType) {
        case OG_IMAGE:
          croppedSize.width = OG_IMAGE_API_CROPPED_WIDTH
          croppedSize.height = OG_IMAGE_API_CROPPED_HEIGHT
          break
        case COVER_IMAGE:
        default:
          croppedSize.width = COVER_IMAGE_API_CROPPED_WIDTH
          croppedSize.height = COVER_IMAGE_API_CROPPED_HEIGHT
          break
      }
      formData.append('image', file)
      formData.append('croppedSize', JSON.stringify(croppedSize))
      const payload = { body: formData }

      const handleImageFileArgs = {
        file,
        setSelectedFiles,
        id,
        onImageAction,
        actionName: 'upload',
        target,
        payload,
        updateImageIds,
        updateCoverImageId,
        updateOgImageId,
        articleState,
        tempCropPosition: null,
        cropResult: null,
        croppedSize: null,
      }

      await handleImageFile(handleImageFileArgs)
      setImageUploadEndCount((prev) => prev + 1)
    }
  }

  return {
    errorMessages,
    setErrorMessages,
    imageUploadStartCount,
    setImageUploadStartCount,
    imageUploadEndCount,
    setImageUploadEndCount,
    handleFiles,
  }
}
